import { CreateAddressDto, QuestionAnswerDto } from 'api/borrower';
import { ReferentialKey } from 'api/referential';
import { geocodeByPlaceId } from 'react-places-autocomplete';
import { QuestionDto } from 'api/survey';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { ActorType } from 'features/new-actor/enums/actorType';
import {
  resetPendingForm,
  setPendingForm
} from 'features/subscription-process/slices/subscriptionProcessSlice';
import { selectPendingForm } from 'features/subscription-process/slices/selectors';
import { useBorrowerProjectId } from 'features/subscription-process/hooks/useBorrowerProjectId';
import { FormikHelpers, FormikProps } from 'formik';
import {
  applyValidationErrorsToForm,
  parseApiError
} from 'shared/utils/apiValidationErrors';
import { usePanel } from 'hooks/usePanel';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { borrowersAPI } from 'services/swagger-api';
import { surveyAPI } from 'services/swagger-api/surveyApi';
import { FormKey } from 'types/form/formKeyEnum';
import { isLegalEntity } from 'helpers/isLegalEntity';
import {
  ContextCode,
  SurveyName,
  useGenericSurvey
} from '../../hooks/useGenericSurvey';
import { ProfilingMode } from '../../slices/types/enums';
import { toDayInput } from 'core/utils/dates';
import {
  MinimalNaturalPersonFormValues,
  MinimalLegalEntityFormValues,
  minimalNaturalPersonFormConfig,
  minimalLegalEntityFormConfig
} from './minimalBorrowerForm';

export type MinimalBorrowerActorType =
  | ActorType.BorrowerNatural
  | ActorType.BorrowerLegal;

/**
 * Maps a backend validation member path (e.g. `naturalPerson.address.googlePlaceId`
 * or `personnePhysique.Adresse`) onto the visible Formik field of the minimal
 * borrower form, so the 400 errors render red on the right input.
 */
const resolveMinimalBorrowerField = (member: string): string | undefined => {
  const m = member.toLowerCase();
  // L'input visible de l'adresse est `addressSearch` (l'autocomplete Google).
  if (m.includes('address') || m.includes('adresse')) return 'addressSearch';
  if (m.includes('email')) return 'email';
  if (m.includes('prenom') || m.includes('firstname')) return 'firstName';
  if (m.includes('lastname') || m.includes('nom')) return 'lastName';
  if (m.includes('birthdate') || m.includes('naissance')) return 'birthDate';
  if (m.includes('civilit')) return 'civility';
  return undefined;
};

interface UseMinimalBorrowerFormProps {
  onSuccess?: (actorId: string) => void;
  defaultActorType?: MinimalBorrowerActorType;
  personId?: string;
  /** Id du projet fourni par l'ouvrant (prioritaire sur l'URL). */
  borrowerProjectId?: string;
}

export const useMinimalBorrowerForm = ({
  onSuccess,
  defaultActorType = ActorType.BorrowerNatural,
  personId,
  borrowerProjectId: borrowerProjectIdProp
}: UseMinimalBorrowerFormProps = {}) => {
  const dispatch = useAppDispatch();
  const { setPanelActions, closePanel } = usePanel();
  const { entity: pendingEntity, formKey } = useAppSelector(selectPendingForm());
  // Id du projet : prop de l'ouvrant en priorité (fiable dans un panneau), sinon l'URL.
  // Jamais le store Redux (`riskId` = emprunteurId, pas le projet).
  const borrowerProjectIdFromUrl = useBorrowerProjectId();
  const borrowerProjectId = borrowerProjectIdProp ?? borrowerProjectIdFromUrl;
  const civilityReferentialData = useAppSelector(
    selectReferentialDataByKey(ReferentialKey.Civility)
  );
  const { getSurvey, initSurvey } = useGenericSurvey();

  const [actorType, setActorType] =
    useState<MinimalBorrowerActorType>(defaultActorType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPersonId, setCurrentPersonId] = useState<string | undefined>(
    personId
  );
  const [currentBorrowerId, setCurrentBorrowerId] = useState<
    string | undefined
  >();
  const [surveyId, setSurveyId] = useState<string>();
  const [questions, setQuestions] = useState<QuestionDto[]>([]);
  const [existingAnswers, setExistingAnswers] = useState<QuestionAnswerDto[]>(
    []
  );
  const [fetchedExistingOutstanding, setFetchedExistingOutstanding] = useState<number | undefined>();
  const [resolvedFormattedAddress, setResolvedFormattedAddress] = useState<string | undefined>();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const naturalPersonFormikRef =
    useRef<FormikProps<MinimalNaturalPersonFormValues>>(null);
  const legalEntityFormikRef =
    useRef<FormikProps<MinimalLegalEntityFormValues>>(null);

  const isNaturalPerson = actorType === ActorType.BorrowerNatural;
  const isEdit = !!currentPersonId;

  const formikRef = isNaturalPerson
    ? naturalPersonFormikRef
    : legalEntityFormikRef;

  // Initialize survey
  useEffect(() => {
    initSurvey(
      ContextCode.BorrowerPerson,
      SurveyName.BorrowerPerson,
      ProfilingMode.Group
    );
  }, [initSurvey]);

  // Fetch survey questions
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const survey = await getSurvey(
          ContextCode.BorrowerPerson,
          SurveyName.BorrowerPerson
        );
        if (survey?.id) {
          setSurveyId(survey.id);
          // Fetch questions for this survey using survey API
          const questionsResponse = await surveyAPI.getSurveyQuestions({
            id: survey.id
          });
          if (questionsResponse.questions) {
            setQuestions(questionsResponse.questions);
          }
        }
      } catch (error) {
        console.error('Error fetching survey:', error);
      }
    };
    fetchSurvey();
  }, [getSurvey]);

  // Detect actor type from pending entity
  useEffect(() => {
    if (pendingEntity) {
      const isLegal = isLegalEntity(pendingEntity);
      setActorType(
        isLegal ? ActorType.BorrowerLegal : ActorType.BorrowerNatural
      );
      if (pendingEntity.id) {
        setCurrentPersonId(pendingEntity.id);
      }
    }
  }, [pendingEntity]);

  // Fetch existing person data when editing
  useEffect(() => {
    const fetchPersonData = async () => {
      if (!currentPersonId || !borrowerProjectId) return;

      setIsLoading(true);
      try {
        const response = await borrowersAPI.getBorrowerProjectPerson({
          borrowerProjectId,
          personId: currentPersonId,
          includeQuestions: true
        });

        if (response.naturalPerson) {
          const person = response.naturalPerson;
          setCurrentBorrowerId(person.borrowerId);

          // Resolve formatted address from Google if placeId is available
          if (person.address?.googlePlaceId) {
            try {
              const results = await geocodeByPlaceId(person.address.googlePlaceId);
              if (results.length > 0) {
                setResolvedFormattedAddress(results[0].formatted_address);
              }
            } catch {
              // Fallback handled in initialValues
            }
          }

          // Set pending entity for form pre-fill
          dispatch(
            setPendingForm({
              entity: person,
              formKey: FormKey.Default
            })
          );
        } else if (response.legalEntity) {
          const entity = response.legalEntity;
          setActorType(ActorType.BorrowerLegal);

          dispatch(
            setPendingForm({
              entity: entity,
              formKey: FormKey.Default
            })
          );
        }

        // Set existing outstanding from root level
        setFetchedExistingOutstanding(response.existingOutstanding ?? undefined);

        // Set existing answers
        if (response.saveAnswersCommand?.answers) {
          setExistingAnswers(response.saveAnswersCommand.answers);
        }
      } catch (error: any) {
        console.error('Error fetching person data:', error);
        toast.error(
          error?.response?.error?.message ||
            'Erreur lors du chargement des données'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchPersonData();
  }, [currentPersonId, borrowerProjectId, dispatch]);

  // Map pending entity to form values
  const initialValues = useMemo(() => {
    // FormKey.Default means entity was selected from search (not form values)
    if (pendingEntity && formKey === FormKey.Default) {
      if (isLegalEntity(pendingEntity)) {
        const entity = pendingEntity as any;
        return {
          id: entity.id,
          borrowerId: currentBorrowerId,
          companyName: entity.companyName || '',
          email: entity.email || '',
          surveyAnswers: existingAnswers
        } as MinimalLegalEntityFormValues;
      } else {
        const person = pendingEntity as any;
        return {
          id: person.id,
          borrowerId: person.borrowerId || currentBorrowerId,
          civility:
            civilityReferentialData?.find(
              (c: any) => c.id === person.civilityId
            )?.code || '',
          lastName: person.lastName || '',
          firstName: person.firstName || '',
          email: person.email || '',
          // Date calendaire : découpe de string, jamais Date -> toISOString (décale d'un jour)
          birthDate: toDayInput(person.birthDate),
          addressSearch: resolvedFormattedAddress
            || (person.address
              ? [person.address.line1, person.address.city, 'France'].filter(Boolean).join(', ')
              : ''),
          address: person.address
            ? {
                address: person.address.line1,
                additionalAddress: person.address.line2 || person.address.line3 || undefined,
                postalCode: person.address.zipCode,
                city: person.address.city,
                country: person.address.country,
                googlePlaceId: person.address.googlePlaceId,
                latitude: person.address.latitude,
                longitude: person.address.longitude
              }
            : undefined,
          existingOutstanding: fetchedExistingOutstanding,
          surveyAnswers: existingAnswers
        } as MinimalNaturalPersonFormValues;
      }
    }

    return isNaturalPerson
      ? { ...minimalNaturalPersonFormConfig.initialValues, surveyAnswers: [] }
      : { ...minimalLegalEntityFormConfig.initialValues, surveyAnswers: [] };
  }, [
    pendingEntity,
    formKey,
    isNaturalPerson,
    existingAnswers,
    currentBorrowerId,
    fetchedExistingOutstanding,
    resolvedFormattedAddress,
    civilityReferentialData
  ]);

  // Form key for Formik re-initialization
  const formUniqueKey = useMemo(() => {
    if (currentPersonId) return `${actorType}-${currentPersonId}`;
    return `${actorType}-new`;
  }, [actorType, currentPersonId]);

  // Delete confirmation handlers
  const openDeleteConfirm = useCallback(() => {
    setIsDeleteConfirmOpen(true);
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    setIsDeleteConfirmOpen(false);
  }, []);

  // Delete handler
  const handleConfirmDelete = useCallback(async () => {
    if (!borrowerProjectId || !currentBorrowerId) return;

    try {
      await borrowersAPI.deleteBorrower({
        borrowerProjectId,
        borrowerId: currentBorrowerId
      });
      toast.success('Emprunteur supprimé avec succès');
      onSuccess?.('');
      dispatch(resetPendingForm());
      closePanel();
    } catch (error: any) {
      console.error('Error deleting borrower:', error);
      toast.error(
        error?.response?.error?.message || 'Erreur lors de la suppression'
      );
    }
  }, [borrowerProjectId, currentBorrowerId, onSuccess, dispatch, closePanel]);

  // Panel actions
  useEffect(() => {
    const basePanelActions = {
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: !isFormValid || isSubmitting || isLoading,
      onSubmit: () => {
        formikRef.current?.submitForm();
      },
      onClose: () => {
        dispatch(resetPendingForm());
      }
    };

    if (isEdit && currentBorrowerId) {
      setPanelActions({
        ...basePanelActions,
        onDelete: openDeleteConfirm,
        deleteLabel: 'COMMON.DELETE',
        deleteDisabled: isSubmitting
      });
    } else {
      setPanelActions(basePanelActions);
    }
  }, [
    dispatch,
    isFormValid,
    isSubmitting,
    isLoading,
    setPanelActions,
    formikRef,
    isEdit,
    currentBorrowerId,
    openDeleteConfirm
  ]);

  // Submit handler for natural person
  const handleNaturalPersonSubmit = useCallback(
    async (
      values: MinimalNaturalPersonFormValues,
      formikHelpers: FormikHelpers<MinimalNaturalPersonFormValues>
    ) => {
      if (!borrowerProjectId) {
        toast.error("Projet d'emprunt introuvable (id manquant).");
        return;
      }
      if (!surveyId) {
        toast.error('Questionnaire non chargé, veuillez réessayer.');
        return;
      }

      setIsSubmitting(true);
      try {
        const address: CreateAddressDto = {
          line1: values.address?.address || '',
          line2: values.address?.additionalAddress?.trim() || '',
          zipCode: values.address?.postalCode || '',
          city: values.address?.city || '',
          country: values.address?.country || '',
          // `addressSearch` contient l'adresse formatée renvoyée par Google.
          formattedAddress: values.addressSearch || undefined,
          googlePlaceId: values.address?.googlePlaceId,
          latitude: values.address?.latitude,
          longitude: values.address?.longitude
        };

        const response = await borrowersAPI.createOrUpdateMinimalBorrowerPerson(
          {
            borrowerProjectId,
            createOrUpdateMinimalBorrowerPersonCommandDto: {
              naturalPerson: {
                id: values.id || undefined,
                borrowerId: values.borrowerId || currentBorrowerId,
                civilityId:
                  civilityReferentialData?.find(
                    (c: any) => c.code === values.civility
                  )?.id || '',
                lastName: values.lastName,
                firstName: values.firstName,
                email: values.email,
                birthDate: new Date(values.birthDate),
                address
              },
              saveAnswersCommand: {
                answers: values.surveyAnswers,
                surveyId,
                autoEvaluate: true
              },
              existingOutstanding: values.existingOutstanding ?? null
            }
          }
        );

        if (response.isSuccess && response.id) {
          toast.success(
            isEdit
              ? 'Emprunteur mis à jour avec succès'
              : 'Emprunteur créé avec succès'
          );
          onSuccess?.(response.id);
          dispatch(resetPendingForm());
          closePanel();
        } else {
          toast.error(response.errorMessage || 'Erreur lors de la sauvegarde');
        }
      } catch (error: any) {
        console.error('Error saving natural person:', error);

        // Remonte les erreurs de validation du back (400) sur les champs Formik
        // concernés (ex. place_id manquant -> champ adresse en rouge).
        const parsed = await parseApiError(error);
        const matchedFields = applyValidationErrorsToForm({
          validationErrors: parsed.validationErrors,
          setFieldError: formikHelpers.setFieldError,
          setFieldTouched: formikHelpers.setFieldTouched,
          resolveField: resolveMinimalBorrowerField
        });

        toast.error(
          parsed.message ||
            (matchedFields > 0
              ? 'Veuillez corriger les champs en erreur'
              : "Erreur lors de la sauvegarde de l'emprunteur")
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      borrowerProjectId,
      surveyId,
      currentBorrowerId,
      isEdit,
      onSuccess,
      dispatch,
      closePanel,
      civilityReferentialData
    ]
  );

  // Submit handler for legal entity
  const handleLegalEntitySubmit = useCallback(
    async (values: MinimalLegalEntityFormValues) => {
      if (!borrowerProjectId) {
        toast.error("Projet d'emprunt introuvable (id manquant).");
        return;
      }
      if (!surveyId) {
        toast.error('Questionnaire non chargé, veuillez réessayer.');
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await borrowersAPI.createOrUpdateMinimalBorrowerPerson(
          {
            borrowerProjectId,
            createOrUpdateMinimalBorrowerPersonCommandDto: {
              legalEntity: {
                id: values.id || undefined,
                companyName: values.companyName,
                email: values.email
              },
              saveAnswersCommand: {
                answers: values.surveyAnswers,
                surveyId,
                autoEvaluate: true
              }
            }
          }
        );

        if (response.isSuccess && response.id) {
          toast.success(
            isEdit
              ? 'Personne morale mise à jour avec succès'
              : 'Personne morale créée avec succès'
          );
          onSuccess?.(response.id);
          dispatch(resetPendingForm());
          closePanel();
        } else {
          toast.error(response.errorMessage || 'Erreur lors de la sauvegarde');
        }
      } catch (error: any) {
        console.error('Error saving legal entity:', error);
        toast.error(
          error?.response?.error?.message ||
            'Erreur lors de la sauvegarde de la personne morale'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [borrowerProjectId, surveyId, isEdit, onSuccess, dispatch, closePanel]
  );

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  const handleActorTypeChange = useCallback(
    (newActorType: MinimalBorrowerActorType) => {
      setActorType(newActorType);
      setCurrentPersonId(undefined);
      setCurrentBorrowerId(undefined);
      setIsFormValid(false);
      setExistingAnswers([]);
      dispatch(resetPendingForm());
    },
    [dispatch]
  );

  const handleSurveyAnswersChange = useCallback(
    (answers: QuestionAnswerDto[]) => {
      // Update formik values with new answers
      if (formikRef.current) {
        formikRef.current.setFieldValue('surveyAnswers', answers);
      }
    },
    [formikRef]
  );

  return {
    actorType,
    setActorType: handleActorTypeChange,
    isNaturalPerson,
    isEdit,
    isLoading,
    initialValues,
    formUniqueKey,
    naturalPersonFormikRef,
    legalEntityFormikRef,
    handleNaturalPersonSubmit,
    handleLegalEntitySubmit,
    handleValidationChange,
    handleSurveyAnswersChange,
    isSubmitting,
    pendingEntity,
    // Survey data
    surveyId,
    questions,
    existingAnswers,
    // Delete confirmation
    isDeleteConfirmOpen,
    closeDeleteConfirm,
    handleConfirmDelete
  };
};
